import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Popup, PopupContent, PopupTrigger } from '@atlaskit/popup/experimental';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { MenuList } from '@atlassian/navigation-system';
import { MenuButtonItem } from '@atlassian/navigation-system/side-nav/menu-button-item';

import { MoreMenuSection } from './more-menu-section';
import type { MoreMenuPopupProps } from './types';

const styles = cssMap({
	popupContentContainerStyles: {
		position: 'relative',
		marginInline: token('space.negative.050'),
	},
	popupContentStyles: {
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
});

export function MoreMenuPopup({
	productItemsData,
	appItemsData,
	moreMenuItemComponent,
	customizeSidebarMenuItem,
	moreMenuItemLabel,
	onClickMoreMenuTrigger,
	testId,
}: MoreMenuPopupProps) {
	const [isOpen, setIsOpen] = useState(false);
	const closeMenu = () => setIsOpen(false);

	const onClick = useCallback(() => {
		setIsOpen(true), onClickMoreMenuTrigger && onClickMoreMenuTrigger();
	}, [onClickMoreMenuTrigger]);

	return (
		<Popup isOpen={isOpen}>
			<PopupTrigger>
				{(triggerProps) => (
					<MenuButtonItem
						{...triggerProps}
						elemBefore={<ShowMoreHorizontalIcon label="" color="currentColor" spacing="spacious" />}
						onClick={onClick}
						testId={testId}
					>
						{moreMenuItemLabel}
					</MenuButtonItem>
				)}
			</PopupTrigger>
			{/* Adding listitem role as the parent is a UL */}
			<Box xcss={styles.popupContentContainerStyles} role="listitem">
				<PopupContent
					shouldFitContainer
					placement="bottom-start"
					// Offset the top margin of the menu section.
					// This will keep an even spacing of menu items between those inside the dropdown and those above,
					// and keep the icons aligned
					offset={[-4, 4]}
					onClose={() => setIsOpen(false)}
					shouldRenderToParent
					strategy="absolute"
				>
					{() => (
						<Box xcss={styles.popupContentStyles}>
							<MenuList>
								<MoreMenuSection
									itemsData={productItemsData}
									moreMenuItemComponent={moreMenuItemComponent}
									closeMenu={closeMenu}
								/>
								<MoreMenuSection
									itemsData={appItemsData}
									moreMenuItemComponent={moreMenuItemComponent}
									closeMenu={closeMenu}
								/>
								{customizeSidebarMenuItem}
							</MenuList>
						</Box>
					)}
				</PopupContent>
			</Box>
		</Popup>
	);
}
